import React from 'react';
import { motion, useInView } from 'framer-motion';
import { Truck, Clock, MapPin } from 'lucide-react';
import heroImage from '../assets/cvj-freight-convoy1.jpeg'; // Replace with your truck image path
import convoyImage from '../assets/cvj-freight-convoy.jpeg'; // Replace with your convoy image path
import './AboutSection.css';

const AboutSection = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });

  const whyChooseUs = [
    { icon: <Truck size={32} />, title: 'Reliable Fleet', desc: 'Modern trucks ensuring timely deliveries.' },
    { icon: <Clock size={32} />, title: '24/7 Service', desc: 'Round-the-clock logistics support.' },
    { icon: <MapPin size={32} />, title: 'UK-Wide Reach', desc: 'Coverage from London to Glasgow.' },
  ];

  const stats = [
    { label: 'Miles Covered', value: '1M', suffix: '+' },
    { label: 'Deliveries Made', value: '10K', suffix: '+' },
    { label: 'Clients Served', value: '200', suffix: '+' },
  ];

  const cardVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
  };

  return (
    <section id="about" className="about-section">
      <div className="about-container">
        {/* Hero Section with Image */}
        <motion.div
          className="about-hero"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1.2 }}
          viewport={{ once: true }}
          style={{ backgroundImage: `url(${heroImage})` }} // Inline style for custom image
        >
          <div className="hero-overlay">
            <h1 className="hero-title">CVJ Services Ltd</h1>
            <p className="hero-subtitle">Driving UK Freight Forward Since 2025</p>
          </div>
        </motion.div>

        {/* Content Section */}
        <div className="about-content-wrapper" ref={ref}>
          <motion.div
            className="about-intro"
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            style={{ backgroundImage: `url(${convoyImage})`, height:"100%" }} // Inline style for intro image
          >
            <h2 className="intro-title">Who We Are</h2>
            <p className="intro-text">
              Founded in 2025, CVJ Services Ltd is Northampton’s premier freight transport provider. With a passion for logistics and a cutting-edge fleet, we deliver goods across the UK with speed and precision. From heavy haulage to refrigerated transport, we’re your trusted partner on the road.
            </p>
          </motion.div>

          {/* Why Choose Us */}
          <div className="why-choose-us">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                className="why-card"
                variants={cardVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                transition={{ delay: index * 0.2 }}
                whileHover={{ scale: 1.05, boxShadow: '0 10px 30px rgba(0, 221, 235, 0.4)' }}
              >
                <div className="why-icon">{item.icon}</div>
                <h3 className="why-title">{item.title}</h3>
                <p className="why-desc">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-card"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: index * 0.3 }}
              >
                <div className="stat-value">{stat.value}{stat.suffix}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.a
            href="#contact"
            className="about-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
            whileHover={{ scale: 1.05, backgroundColor: '#FF007A' }}
            whileTap={{ scale: 0.95 }}
          >
            Get a Quote
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;